import React, { type ReactNode, useEffect, useRef } from 'react';

import { FormattedMessage } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import Tooltip from '@atlaskit/tooltip';

import {
	FeatureExposedAnalytics,
	useFeatureExposedAnalytics,
} from '../analytics/feature-exposed-analytics';
import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { getLoggedInPage } from '../services/get-logged-in-page';
import { isEligibleForConfluenceBuyReverseTrialExperiment } from '../services/is-eligible-for-confluence-buy-reverse-trials';
import { isEligibleForEduDiscount } from '../services/is-eligible-for-edu-discount';
import { isEligibleForStandardTrialsUserAndStorageLimitBreakers } from '../services/is-eligible-for-standard-trials-user-and-storage-limit-breakers';
import { WAC_URL } from '../services/wac-urls';
import type { ExperimentCohort, ExperimentCohortControlVariation } from '../types';

import { AutomationLimitsDialog } from './dialogs/automation-limits-modal';
import { EducationalDiscountDialog } from './dialogs/educational-discount-modal';
import { FreeToStandardDialog } from './dialogs/free-to-standard';
import { FreeToStandardRefresh } from './dialogs/free-to-standard-refresh';
import { PaymentDetailsPopupDialog } from './dialogs/payment-details-popup';
import { PlanSelectionPageDialog } from './dialogs/plan-selection-page-modal';
import { PremiumAddPayment } from './dialogs/premium-add-payment';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPayment } from './dialogs/standard-add-payment';
import { StandardTrialLimitBreakersDialog } from './dialogs/standard-trial-limit-breakers-modal';
import { EditionAwarenessLink } from './edition-awareness-button';

const AddPaymentLink = () => {
	const context = useEditionAwareness();
	const { addPaymentDetailsExperienceUrl } = context;

	// This information should always be available, given the prior checks to get here
	if (!addPaymentDetailsExperienceUrl) {
		return <NoAction />;
	}

	return (
		<Tooltip
			content={
				<FormattedMessage
					id="@atlassian/edition-awareness.link.add-payment-tooltip"
					defaultMessage="Add payment details to keep your subscription"
					description="Tooltip for add payment details button"
				/>
			}
			position="bottom"
		>
			<EditionAwarenessLink
				href={addPaymentDetailsExperienceUrl}
				target="_blank"
				touchpointIntent="addPaymentDetails"
				linkId="predunning"
			/>
		</Tooltip>
	);
};

const JiraStandardTrialOverviewLink = () => {
	const { cloudId, environment, billingSourceSystem, userEmail } = useEditionAwareness();

	// This information should always be available, given the prior checks to get here
	if (!environment || !billingSourceSystem || !userEmail) {
		return <NoAction />;
	}

	const rawJstoUrl =
		`${WAC_URL[environment]}/software/jira?ref=edition_awareness_standard_trial_` +
		`${billingSourceSystem.toLowerCase()}&cloudSiteId=${cloudId}`;
	const jstoUrl = getLoggedInPage(userEmail, rawJstoUrl);

	return (
		<Tooltip
			content={
				<FormattedMessage
					id="@atlassian/edition-awareness.link.jira-standard-trial-overview-tooltip"
					defaultMessage="More about your trial and how to pay"
					description="Tooltip for trial information button"
				/>
			}
			position="bottom"
		>
			<EditionAwarenessLink
				href={jstoUrl}
				target="_blank"
				touchpointIntent="addPaymentDetails"
				linkId="jsto"
			/>
		</Tooltip>
	);
};

/**
 * Sends an event when there is no action available for the customer to render. This is an error state
 * which means that audience and timing has passed a customer as eligible when they shouldn't have.
 *
 * Next iteration on this would be to return a safe default action for all customers
 */
function NoAction() {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'actionsAndContent',
			actionSubjectId: 'editionAwareness',
			action: 'missed',
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent]);

	return null;
}

function FreeEdition() {
	const context = useEditionAwareness();
	const { product, siteType, isInTeamWorkCollection } = context;

	if (
		['jira', 'confluence'].includes(product) &&
		isInTeamWorkCollection &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageDialog type="twc" />;
	}

	if (
		isEligibleForEduDiscount(context) &&
		FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'offer_edu_org_discount',
			'cohort',
			'not-enrolled',
		) === 'variation'
	) {
		return (
			<FeatureExposedAnalytics flagKey="offer_edu_org_discount" cohort="variation">
				<EducationalDiscountDialog />
			</FeatureExposedAnalytics>
		);
	}

	if (product === 'jira' && fg('growth_automation_limits')) {
		if (
			FeatureGates.getExperimentValue<string>(
				'growth_automation_limits_experiment',
				'cohort',
				'not-enrolled',
			) === 'variation'
		) {
			return (
				<FeatureExposedAnalytics flagKey="growth_automation_limits" cohort="variation">
					<AutomationLimitsDialog />
				</FeatureExposedAnalytics>
			);
		}
	}

	if (
		product === 'jira' &&
		// We're excluding the edu experiment cohort from seeing the refresh experiment
		siteType !== 'EDU' &&
		siteType !== 'ORG' &&
		FeatureGates.getExperimentValue<string>(
			'jira_free_ea_modal_refresh',
			'cohort',
			'not-enrolled',
			{
				// We only want to fire the exposure when they view the modal.
				// The control experience is still inside of the product.
				fireExperimentExposure: false,
			},
		) === 'variation'
	) {
		return <FreeToStandardRefresh />;
	}

	return <FreeToStandardDialog />;
}

function StandardEdition({ trialButton }: { trialButton: ReactNode }) {
	const context = useEditionAwareness();
	const {
		product,
		isInTrial,
		isInPredunning,
		isInvoiceable,
		canAddPaymentDetails,
		isInTeamWorkCollection,
		userRoles,
	} = context;

	if (
		['jira', 'confluence'].includes(product) &&
		isInTeamWorkCollection &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageDialog type="twc" />;
	}

	if ((isInTrial || isInPredunning) && isInvoiceable === false && canAddPaymentDetails) {
		if (
			// Only eligible for Confluence and Jira
			isEligibleForStandardTrialsUserAndStorageLimitBreakers({
				context,
			})
		) {
			const abExperimentKey =
				product === 'jira'
					? 'jira_edition_awareness_standard_trials'
					: 'connie_edition_awareness_standard_trials';

			const abCohort = FeatureGates.getExperimentValue<ExperimentCohort>(
				abExperimentKey,
				'cohort',
				'not-enrolled',
			);

			if (abCohort === 'treatment') {
				return (
					<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort}>
						<StandardTrialLimitBreakersDialog />
					</FeatureExposedAnalytics>
				);
			}

			if (product === 'confluence' && fg('new_ea_confluence_standard')) {
				return (
					<>
						<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
						<StandardAddPayment />
					</>
				);
			}

			// Jira Standard - Site admins get routed to JSTO, others to payment popup
			if (
				product === 'jira' &&
				isInTrial &&
				userRoles?.includes('SITE_ADMIN') &&
				fg('new_ea_jsto_link')
			) {
				return (
					<>
						<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
						<JiraStandardTrialOverviewLink />
					</>
				);
			}
			if (
				product === 'jira' &&
				isInTrial &&
				!userRoles?.includes('SITE_ADMIN') &&
				fg('new_ea_jira_payment_details_popup')
			) {
				return (
					<>
						<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
						<PaymentDetailsPopupDialog />
					</>
				);
			}

			if (!trialButton) {
				return <NoAction />;
			}

			return (
				<>
					<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
					{trialButton}
				</>
			);
		}

		if (
			isInTrial &&
			(product === 'jira-service-management' || product === 'jira-product-discovery')
		) {
			return <StandardAddPayment />;
		}

		// Jira Standard - Site admins get routed to JSTO, others to payment popup
		if (
			product === 'jira' &&
			isInTrial &&
			userRoles?.includes('SITE_ADMIN') &&
			fg('new_ea_jsto_link')
		) {
			return <JiraStandardTrialOverviewLink />;
		}
		if (
			product === 'jira' &&
			isInTrial &&
			!userRoles?.includes('SITE_ADMIN') &&
			fg('new_ea_jira_payment_details_popup')
		) {
			return <PaymentDetailsPopupDialog />;
		}

		if (product === 'confluence' && fg('new_ea_confluence_standard')) {
			return <StandardAddPayment />;
		}

		if (
			isInPredunning &&
			['jira', 'jira-service-management', 'jira-product-discovery'].includes(product) &&
			fg('new_ea_jsw_jsm_jpd_pre_dunning')
		) {
			return <AddPaymentLink />;
		}

		if (!trialButton) {
			return <NoAction />;
		}

		// trialButton is returned for all other products + Confluence and Jira sites not eligible
		return trialButton;
	}

	return <NoAction />;
}

function PremiumEdition({ trialButton }: { trialButton?: ReactNode }) {
	const context = useEditionAwareness();
	const fireExperimentExposureEvent = useFeatureExposedAnalytics();

	const {
		product,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		isInTeamWorkCollection,
		addPaymentDetailsExperienceUrl,
	} = context;

	if (
		product === 'jira' &&
		(isInTrial || isInPredunning) &&
		isInReverseTrial &&
		fg('jira_reverse_trial_ea_updates_during_trial_gate')
	) {
		if (
			FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
				'jira_reverse_trial_ea_updates_during_trial',
				'cohort',
				'not-enrolled',
			) === 'variation'
		) {
			return (
				<FeatureExposedAnalytics
					flagKey="jira_reverse_trial_ea_updates_during_trial"
					cohort="variation"
				>
					<PlanSelectionPageDialog type="reverse-trial" />
				</FeatureExposedAnalytics>
			);
		}
	}

	if (
		['jira', 'confluence'].includes(product) &&
		isInTeamWorkCollection &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageDialog type="twc" />;
	}

	if (
		product === 'jira' &&
		isInTrial &&
		isInReverseTrial &&
		fg('new_ea_jira_reverse_trial_dropdown')
	) {
		// We can remove this check once we remove the final trialButton return
		if (!addPaymentDetailsExperienceUrl) {
			return <NoAction />;
		}

		return <ReverseTrialPopupDialog />;
	}

	if (
		product === 'jira' &&
		isInTrial &&
		!isInReverseTrial &&
		fg('new_ea_jira_payment_details_popup')
	) {
		// We can remove this check once we remove the final trialButton return
		if (!addPaymentDetailsExperienceUrl) {
			return <NoAction />;
		}

		return <PaymentDetailsPopupDialog />;
	}

	if (
		isInPredunning &&
		['jira', 'jira-service-management'].includes(product) &&
		fg('new_ea_jsw_jsm_jpd_pre_dunning')
	) {
		return <AddPaymentLink />;
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment({ context }) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const aaCohort = FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'growth_confluence_buy_reverse_trials_aa',
			'cohort',
			'not-enrolled',
		);

		const abCohort = FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'growth_confluence_buy_reverse_trials_ab',
			'cohort',
			'not-enrolled',
		);

		fireExperimentExposureEvent('growth_confluence_buy_reverse_trials_aa', aaCohort, 'luna');
		fireExperimentExposureEvent('growth_confluence_buy_reverse_trials_ab', abCohort, 'luna');

		if (abCohort === 'variation') {
			return <PlanSelectionPageDialog type="reverse-trial" />;
		}
	}

	if (
		product === 'confluence' &&
		(isInTrial || isInPredunning) &&
		fg('new_ea_confluence_premium')
	) {
		return <PremiumPopup />;
	}

	if (isInTrial && product === 'jira-service-management' && fg('new_ea_jsm_premium')) {
		return <PremiumAddPayment />;
	}

	if (!trialButton) {
		return <NoAction />;
	}

	// Until we migrate the rest of the products to the new button content we'll use the old one from product
	return trialButton;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
export function ActionsAndContent({ trialButton }: { trialButton?: ReactNode }) {
	const { edition } = useEditionAwareness();

	if (edition === 'free') {
		return <FreeEdition />;
	}

	if (edition === 'standard') {
		return <StandardEdition trialButton={trialButton} />;
	}

	if (edition === 'premium') {
		return <PremiumEdition trialButton={trialButton} />;
	}

	return <NoAction />;
}
